/**
 * Client-side cache with TTL (Time To Live) support.
 * Uses localStorage for persistence across page reloads.
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// Default TTL values in milliseconds
export const CACHE_TTL = {
  REAL_TIME: 15 * 60 * 1000,     // 15 minutes for price data (no real-time API)
  MARKET_DATA: 15 * 60 * 1000,   // 15 minutes for market pulse
  HOT_MOVERS: 15 * 60 * 1000,    // 15 minutes for hot movers
  HISTORICAL: 15 * 60 * 1000,    // 15 minutes for historical data
  FUNDAMENTALS: 60 * 60 * 1000,  // 1 hour for fundamentals
  STATIC: 24 * 60 * 60 * 1000,   // 24 hours for static data
};

const CACHE_PREFIX = 'marome_cache_';
const memoryCache = new Map<string, CacheEntry<unknown>>();

/**
 * Get item from cache (memory first, then localStorage)
 */
export function getCached<T>(key: string): T | null {
  const fullKey = CACHE_PREFIX + key;

  // Check memory cache first (faster)
  const memEntry = memoryCache.get(fullKey) as CacheEntry<T> | undefined;
  if (memEntry) {
    if (Date.now() - memEntry.timestamp < memEntry.ttl) {
      return memEntry.data;
    }
    // Expired, remove from memory
    memoryCache.delete(fullKey);
  }

  // Check localStorage
  try {
    const stored = localStorage.getItem(fullKey);
    if (stored) {
      const entry: CacheEntry<T> = JSON.parse(stored);
      if (Date.now() - entry.timestamp < entry.ttl) {
        // Still valid, add to memory cache
        memoryCache.set(fullKey, entry);
        return entry.data;
      }
      // Expired, remove from localStorage
      localStorage.removeItem(fullKey);
    }
  } catch {
    // Ignore localStorage errors
  }

  return null;
}

/**
 * Set item in cache (both memory and localStorage)
 */
export function setCache<T>(key: string, data: T, ttl: number = CACHE_TTL.REAL_TIME): void {
  const fullKey = CACHE_PREFIX + key;
  const entry: CacheEntry<T> = {
    data,
    timestamp: Date.now(),
    ttl,
  };

  // Set in memory cache
  memoryCache.set(fullKey, entry);

  // Try to persist to localStorage
  try {
    localStorage.setItem(fullKey, JSON.stringify(entry));
  } catch {
    // localStorage might be full or disabled
  }
}

/**
 * Remove item from cache
 */
export function removeCache(key: string): void {
  const fullKey = CACHE_PREFIX + key;
  memoryCache.delete(fullKey);
  try {
    localStorage.removeItem(fullKey);
  } catch {
    // Ignore
  }
}

/**
 * Clear all cache entries
 */
export function clearCache(): void {
  memoryCache.clear();
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX));
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Ignore
  }
}

/**
 * Clear expired cache entries
 */
export function clearExpiredCache(): void {
  const now = Date.now();

  // Clear memory cache
  for (const [key, entry] of memoryCache.entries()) {
    if (now - entry.timestamp >= entry.ttl) {
      memoryCache.delete(key);
    }
  }

  // Clear localStorage
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX));
    for (const key of keys) {
      const stored = localStorage.getItem(key);
      if (stored) {
        const entry: CacheEntry<unknown> = JSON.parse(stored);
        if (now - entry.timestamp >= entry.ttl) {
          localStorage.removeItem(key);
        }
      }
    }
  } catch {
    // Ignore
  }
}

/**
 * Get or fetch with cache
 */
export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = CACHE_TTL.REAL_TIME
): Promise<T> {
  // Check cache first
  const cached = getCached<T>(key);
  if (cached !== null) {
    return cached;
  }

  // Fetch and cache
  const data = await fetcher();
  setCache(key, data, ttl);
  return data;
}

/**
 * Batch fetch with cache - fetches only uncached items
 */
export async function cachedBatchFetch<T>(
  keys: string[],
  fetcher: (uncachedKeys: string[]) => Promise<Map<string, T>>,
  ttl: number = CACHE_TTL.REAL_TIME
): Promise<Map<string, T>> {
  const results = new Map<string, T>();
  const uncachedKeys: string[] = [];

  // Check cache for each key
  for (const key of keys) {
    const cached = getCached<T>(key);
    if (cached !== null) {
      results.set(key, cached);
    } else {
      uncachedKeys.push(key);
    }
  }

  // Fetch uncached items
  if (uncachedKeys.length > 0) {
    const fetched = await fetcher(uncachedKeys);
    for (const [key, data] of fetched.entries()) {
      setCache(key, data, ttl);
      results.set(key, data);
    }
  }

  return results;
}

// Clear expired cache on startup
clearExpiredCache();

// Periodically clear expired cache (every 5 minutes)
setInterval(clearExpiredCache, 5 * 60 * 1000);
